import {
  decide,
  deriveGenome,
  express,
  initialState,
  kuramotoCouple,
  melody,
  personality,
  sha256hex,
  stateHash,
  step,
  syncOrder,
  type YuragiGenome,
  type YuragiState,
} from 'yuragi-engine';
import {
  artifactContentHash,
  createArtifact,
  decayExp,
  foldBlockEvents,
  GENESIS_EVENT_HASH,
  knowledgeRootHash,
  knowledgeSignal,
  learnFromEvent,
  replayHerdWithMind,
  type FoldedExperienceEvent,
  type KnowledgeStore,
  type RawExperienceEvent,
  type YuragiEventKind,
} from 'yuragi-engine/sdk/mind';
import { CASE_001, characterById } from '../case/case001';
import type {
  ActorMotion,
  CharacterId,
  PersonalityProfile,
  YuragiAction,
  YuragiAgentSnapshot,
  YuragiAgentSystem,
  YuragiExperienceEvent,
  YuragiKnowledgeFact,
} from '../game/types';

const GENESIS_HEIGHT = 320;
const INVESTIGATION_COUPLING = 0.14;
const CHARACTER_IDS = CASE_001.characters.map((character) => character.id);

export const YURAGI_COUPLING = {
  investigation: INVESTIGATION_COUPLING,
  trial: 0.28,
  crisis: 0.46,
  aftermath: 0.1,
} as const;

export interface CourtAgentStimulus {
  targets: CharacterId[] | 'all';
  kind: YuragiEventKind;
  label: string;
  actorId?: string;
  subject?: string;
  predicate?: string;
  value?: unknown;
  intensity?: number;
}

interface MutableAgent {
  characterId: CharacterId;
  id: string;
  identity: string;
  birthHeight: number;
  state: YuragiState;
  experience: [number, number, number, number];
  knowledge: KnowledgeStore;
  eventLog: FoldedExperienceEvent[];
  eventTip: string;
  eventSeq: number;
  lastStimulus: string | null;
}

function clamp01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

function asExperience(value: number[]): [number, number, number, number] {
  return [value[0] ?? 0, value[1] ?? 0, value[2] ?? 0, value[3] ?? 0];
}

function targets(stimulus: CourtAgentStimulus, characterId: CharacterId): boolean {
  return stimulus.targets === 'all' || stimulus.targets.includes(characterId);
}

function rawEvent(stimulus: CourtAgentStimulus): RawExperienceEvent {
  const payload: Record<string, unknown> = {
    record: stimulus.label,
    intensity: clamp01(stimulus.intensity ?? 0.5),
  };
  if (stimulus.subject !== undefined) payload.subject = stimulus.subject;
  if (stimulus.predicate !== undefined) payload.predicate = stimulus.predicate;
  if (stimulus.value !== undefined) payload.value = stimulus.value;
  return {
    kind: stimulus.kind,
    actorId: stimulus.actorId ?? 'hotei',
    payload,
  };
}

function actionMotion(action: YuragiAction, arousal: number, personalityState: PersonalityProfile): ActorMotion {
  if (arousal > 0.74 && personalityState.boldness > personalityState.calm) return 'angry';
  const motions: Record<YuragiAction, ActorMotion> = {
    explore: 'point',
    rest: 'idle',
    greet: 'talk',
    create: 'point',
    wander: 'walk',
    sing: 'talk',
    recall: 'talk',
    craft: 'point',
  };
  return motions[action];
}

function actionStance(action: YuragiAction, arousal: number): string {
  if (arousal > 0.82) return 'volatile';
  if (arousal > 0.64) return 'strained';
  const stances: Record<YuragiAction, string> = {
    explore: 'scanning the record',
    rest: 'holding composure',
    greet: 'seeking rapport',
    create: 'constructing an account',
    wander: 'avoiding the center',
    sing: 'projecting confidence',
    recall: 'searching memory',
    craft: 'assembling a response',
  };
  return stances[action];
}

function mutableFromSnapshot(agent: YuragiAgentSnapshot): MutableAgent {
  return {
    characterId: agent.characterId,
    id: agent.id,
    identity: agent.identity,
    birthHeight: agent.birthHeight,
    state: { ...agent.state, mem: [...agent.state.mem] },
    experience: [...agent.experience] as [number, number, number, number],
    knowledge: { ...agent.knowledge } as KnowledgeStore,
    eventLog: agent.eventLog.map((event) => ({ ...event, payload: { ...event.payload } })),
    eventTip: agent.eventTip,
    eventSeq: agent.eventSeq,
    lastStimulus: agent.lastStimulus,
  };
}

function refreshAgent(agent: MutableAgent): YuragiAgentSnapshot {
  const character = characterById[agent.characterId];
  const genome = deriveGenome(agent.identity);
  const signal = knowledgeSignal(genome, agent.knowledge, agent.state.n);
  const currentPersonality = personality(genome, agent.state, agent.experience) as PersonalityProfile;
  const currentDecision = decide(genome, agent.state, agent.experience, signal);
  const root = knowledgeRootHash(genome, agent.knowledge, agent.state.n);
  const mind = agent.eventSeq > 0 ? { exp: agent.experience, knowledgeRoot: root } : null;
  const hash = stateHash(genome, agent.state, mind);
  const output = express(
    { id: agent.id, identity: agent.identity, name: character.name, genome, state: agent.state },
    agent.lastStimulus ? 'react' : 'greet',
    agent.lastStimulus ?? '',
  );
  const signature = melody(genome, agent.state, 1);
  const lastIntensity = Number(agent.eventLog.at(-1)?.payload.intensity ?? 0);
  const kineticEnergy = Math.min(1, Math.abs(agent.state.thetaDot));
  const arousal = clamp01(
    (1 - currentPersonality.calm) * 0.48 +
    currentPersonality.boldness * 0.24 +
    kineticEnergy * 0.14 +
    lastIntensity * 0.14,
  );
  const trust = clamp01(
    currentPersonality.calm * 0.34 +
    currentPersonality.sociability * 0.38 +
    (1 - arousal) * 0.28,
  );
  const artifact = createArtifact(
    'quest',
    genome,
    agent.state,
    agent.experience,
    agent.knowledge,
    agent.state.n,
  );

  return {
    ...agent,
    state: { ...agent.state, mem: [...agent.state.mem] },
    experience: [...agent.experience] as [number, number, number, number],
    knowledge: { ...agent.knowledge } as Record<string, YuragiKnowledgeFact>,
    eventLog: agent.eventLog as YuragiExperienceEvent[],
    personality: currentPersonality,
    decision: currentDecision.action as YuragiAction,
    decisionScores: { ...currentDecision.scores },
    expression: output.utterance,
    expressionRule: output.bearlang.rule,
    expressionSlot: output.bearlang.slot,
    motion: actionMotion(currentDecision.action as YuragiAction, arousal, currentPersonality),
    stance: actionStance(currentDecision.action as YuragiAction, arousal),
    arousal,
    trust,
    knowledgeRoot: root,
    stateHash: hash,
    proofMessage: `YURAGI|v1|${agent.id}|${agent.state.n}|${hash}`,
    motif: {
      rootName: signature.rootName,
      tempo: signature.tempo,
      midi: signature.notes.slice(0, 4).map((note) => note.midi),
    },
    artifact: {
      title: String(artifact.title ?? artifact.name ?? 'Unresolved record'),
      objective: String(artifact.objective ?? 'preserve the current state'),
      theme: String(artifact.antagonistTrait ?? artifact.theme ?? 'stoic'),
      hash: artifactContentHash(artifact),
    },
  };
}

function buildSystem(
  mutable: MutableAgent[],
  height: number,
  couplingK: number,
  kHistory: Array<{ height: number; k: number }>,
  auditSequence: number,
): YuragiAgentSystem {
  const snapshots = mutable.map(refreshAgent);
  const agents = Object.fromEntries(
    snapshots.map((agent) => [agent.characterId, agent]),
  ) as Record<CharacterId, YuragiAgentSnapshot>;
  const order = syncOrder(snapshots.map((agent) => ({ state: agent.state })));
  const transitionHash = sha256hex([
    'HOTEI',
    'yuragi-court-v1',
    height,
    couplingK.toFixed(6),
    order.toFixed(12),
    ...CHARACTER_IDS.map((id) => agents[id].stateHash),
  ].join('|'));
  return {
    protocol: 'yuragi-court-v1',
    height,
    couplingK,
    kHistory,
    syncOrder: order,
    transitionHash,
    auditSequence,
    agents,
  };
}

export function createYuragiAgentSystem(): YuragiAgentSystem {
  const mutable: MutableAgent[] = CASE_001.characters.map((character) => {
    const genome = deriveGenome(character.identity);
    return {
      characterId: character.id,
      id: genome.identityHash.slice(0, 12),
      identity: character.identity,
      birthHeight: GENESIS_HEIGHT - character.yuragiGenesisTick,
      state: initialState(genome),
      experience: [0, 0, 0, 0],
      knowledge: {},
      eventLog: [],
      eventTip: GENESIS_EVENT_HASH,
      eventSeq: 0,
      lastStimulus: null,
    };
  });

  for (let height = 1; height <= GENESIS_HEIGHT; height += 1) {
    const live = mutable.filter((agent) => agent.birthHeight < height);
    for (const agent of live) {
      agent.state = step(deriveGenome(agent.identity), agent.state);
      agent.experience = asExperience(decayExp(agent.experience));
    }
    kuramotoCouple(live, INVESTIGATION_COUPLING);
  }

  return buildSystem(
    mutable,
    GENESIS_HEIGHT,
    INVESTIGATION_COUPLING,
    [{ height: 1, k: INVESTIGATION_COUPLING }],
    0,
  );
}

export function advanceYuragiAgents(
  system: YuragiAgentSystem,
  stimuli: CourtAgentStimulus[] = [],
  options: { steps?: number; coupling?: number } = {},
): YuragiAgentSystem {
  const mutable = CHARACTER_IDS.map((id) => mutableFromSnapshot(system.agents[id]));
  const steps = Math.max(1, Math.floor(options.steps ?? 1));
  const coupling = options.coupling ?? system.couplingK;
  let height = system.height;
  const kHistory = [...system.kHistory];
  if (Math.abs(coupling - system.couplingK) > 1e-9) {
    kHistory.push({ height: height + 1, k: coupling });
  }

  for (let iteration = 0; iteration < steps; iteration += 1) {
    height += 1;
    const live = mutable.filter((agent) => agent.birthHeight < height);
    for (const agent of live) {
      const genome = deriveGenome(agent.identity);
      agent.state = step(genome, agent.state);
      const relevant = iteration === 0 ? stimuli.filter((stimulus) => targets(stimulus, agent.characterId)) : [];
      if (relevant.length > 0) {
        const folded = foldBlockEvents(
          genome,
          agent.experience,
          relevant.map(rawEvent),
          agent.eventTip,
          agent.eventSeq,
          height,
          agent.state.n,
        );
        agent.experience = asExperience(folded.exp);
        agent.eventLog = [...agent.eventLog, ...folded.folded];
        agent.eventTip = folded.tip;
        agent.eventSeq = folded.seq;
        agent.lastStimulus = relevant.at(-1)?.label ?? agent.lastStimulus;
        for (const event of folded.folded) {
          agent.knowledge = learnFromEvent(genome, agent.knowledge, event);
        }
      } else {
        agent.experience = asExperience(decayExp(agent.experience));
      }
    }
    kuramotoCouple(live, coupling);
  }

  return buildSystem(mutable, height, coupling, kHistory, system.auditSequence + 1);
}

function replayRecords(system: YuragiAgentSystem) {
  return CHARACTER_IDS.map((id) => {
    const agent = system.agents[id];
    return {
      id: agent.id,
      identity: agent.identity,
      genome: deriveGenome(agent.identity),
      birthHeight: agent.birthHeight,
    };
  });
}

function replayLogs(system: YuragiAgentSystem): Record<string, FoldedExperienceEvent[]> {
  return Object.fromEntries(
    CHARACTER_IDS.map((id) => {
      const agent = system.agents[id];
      return [agent.id, agent.eventLog as FoldedExperienceEvent[]];
    }),
  );
}

export function verifyYuragiAgent(system: YuragiAgentSystem, characterId: CharacterId): boolean {
  const agent = system.agents[characterId];
  const replayed = replayHerdWithMind(
    replayRecords(system),
    agent.state.n,
    system.kHistory,
    replayLogs(system),
    agent.id,
  );
  return replayed.tick === agent.state.n && replayed.hash === agent.stateHash;
}

export function verifyYuragiSystem(system: YuragiAgentSystem): boolean {
  return CHARACTER_IDS.every((characterId) => verifyYuragiAgent(system, characterId));
}

export function yuragiAgentTicks(system: YuragiAgentSystem): Record<CharacterId, number> {
  return Object.fromEntries(
    CHARACTER_IDS.map((id) => [id, system.agents[id].state.n]),
  ) as Record<CharacterId, number>;
}

export function yuragiEventCheckpoint(agent: YuragiAgentSnapshot): string {
  return `YURAGI|evt|v1|${agent.id}|${agent.state.n}|${agent.eventSeq}|${agent.eventTip}`;
}

export function yuragiCrisisReady(system: YuragiAgentSystem, characterId: CharacterId): boolean {
  const agent = system.agents[characterId];
  const finalAccusationLearned = Boolean(agent.knowledge['brass-dust::contradicts']);
  return finalAccusationLearned ||
    agent.arousal >= 0.58 ||
    ['wander', 'sing', 'craft'].includes(agent.decision) ||
    agent.personality.boldness > agent.personality.calm;
}
